import { endOfDay, isBefore, isValid, parseISO } from 'date-fns'

import { MANAGER_COACHING_PROGRAM_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/managerCoaching/seatEligibility'
import { ClientLicenseSessionInfo, ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { CONVERSATION_STATE, CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

/** Order the rows render in on the client profile: mental health coaching first, then manager coaching. */
export const AI_CONVERSATION_TOGGLE_TYPES = [CONVERSATION_TYPE.AI_COACH, CONVERSATION_TYPE.MANAGER_COACHING_AI] as const

export type AiConversationToggleType = (typeof AI_CONVERSATION_TOGGLE_TYPES)[number]

export const getAiConversationByType = (
  conversations: ConversationObject[] | undefined,
  type: AiConversationToggleType,
): ConversationObject | undefined =>
  conversations?.find((conversation) => conversation.conversation_attributes?.type === type)

/**
 * A row is shown when the provider's current episode belongs to the conversation. Manager growth
 * coaching AI conversations are not always tied to the coach's episode, so an active one is shown
 * regardless of the episode link.
 */
export const shouldShowAiConversationToggle = (
  conversation: ConversationObject | undefined,
  currentEpisode: Episode | undefined,
): boolean => {
  if (!conversation) {
    return false
  }
  const isLinkedToCurrentEpisode = !!currentEpisode && !!conversation.episode_ids?.includes(currentEpisode.id)
  if (isLinkedToCurrentEpisode) {
    return true
  }
  return (
    conversation.conversation_attributes?.type === CONVERSATION_TYPE.MANAGER_COACHING_AI &&
    conversation.conversation_state === CONVERSATION_STATE.ACTIVE
  )
}

export const getManagerCoachingLicense = (
  client: Partial<Pick<ClientObject, 'seat_based_licenses'>> | undefined,
): ClientLicenseSessionInfo | undefined =>
  client?.seat_based_licenses?.find((license) => license.program_type === MANAGER_COACHING_PROGRAM_TYPE)

/**
 * A license is expired once the calendar day of `access_end_date` has fully passed. The end date
 * is a date-only string, so it is interpreted in the provider's local time zone.
 */
export const isLicenseExpired = (license: Pick<ClientLicenseSessionInfo, 'access_end_date'>, now: Date): boolean => {
  const endDate = parseISO(license.access_end_date)
  return isValid(endDate) && isBefore(endOfDay(endDate), now)
}
